import Base from './Base';
import moment from 'moment-timezone';
import user from '../modules/user';
import User from './User';
import Contact from './Contact';
import MediaCollection from './../collections/MediaCollection';
import urlRegex from 'url-regex-safe';
import PendingMentions from '../collections/PendingMentions';
import twilioLang from '../../lang/en/twilio';

export default class MessageSimple extends Base {
    constructor(item) {
        super(item);

        if (this.user) {
            this.user = new User(this.user);
        }

        if (this.contact) {
            this.contact = new Contact(this.contact);
        }

        if (!this.icon) {
            this.icon = '';
        }

        this.sent_at = item.sent_at || '';
        this.media = Array.isArray(item.media) ? new MediaCollection(item.media).models : [];
        this.previews = item.previews || [];
        this.pending_mentions = new PendingMentions(item.pending_mentions || []).models;
        this.processing = false;
        this.cancelling = false;
    }

    get mediaAttachment() {
        return this.media.filter(item => item.isVcard);
    }

    get identifyKey() {
        const id = this.id;
        const statusCMI = this.statusCurrentMinutesInfo ? 1 : 0;

        return `${id}_${statusCMI}`;
    }

    get bodyParse() {
        if (this.has_tagging && this.user.full_name) {
            return `${this.user.full_name}: ${this.body}`;
        }

        // Added for Conference call timeline
        if (this.body.includes('Conference call ended')) {
            return 'Conference call ended';
        }

        return this.body;
    }

    get createdTimestamp() {
        return moment(this.created_at).valueOf();
    }

    get createdTimestampMinute() {
        return Math.floor(this.createdTimestamp / 1000 / 60);
    }

    get messageTimestamp() {
        const timestamp = this.send_at || this.created_at;

        return moment(timestamp).valueOf();
    }

    get currentDate() {
        if (!this.statusCurrentDate) return false;

        if (
            moment
                .utc(this.created_at)
                .local()
                .dayOfYear() ===
            moment
                .utc()
                .local()
                .dayOfYear()
        ) {
            return 'Today';
        }

        return moment
            .utc(this.created_at)
            .local()
            .format('dddd, MMM D');
    }

    get isVoicemail() {
        if (!this.record) return false;

        return this.record.is_voicemail;
    }

    get msgInfo() {
        let time = null;
        let prefix = '';

        if (this.hasRetryManualStatus) {
            prefix = 'Queued ';
            time = moment.utc(this.queued_at);
        } if (this.hasScheduled) {
            prefix = 'Scheduled for ';
            time = moment.utc(this.send_at);
        } else if (this.automated) {
            prefix = 'Automatically sent ';
            time = moment.utc(this.sent_at);
        } else if (this.type === 'note') {
            prefix = 'Note sent';
            time = moment.utc(this.sent_at);
        } else if (this.received_at) {
            prefix = 'Received ';
            time = moment.utc(this.received_at);
        } else if (this.failed_at) {
            prefix = 'Failed ';
            time = moment.utc(this.failed_at);
        } else if (this.hasStatusQueued) {
            prefix = 'Queued ';
            time = moment.utc(this.queued_at);
        } else if (this.hasStatusRetry) {
            prefix = 'Queued ';
            time = moment.utc(this.created_at);
        } else if (this.status === 'sent' || this.status === 'legacy' || this.status === 'faked') {
            prefix = 'Sent ';
            time = moment.utc(this.sent_at);
        } else if (this.status === 'delivered') {
            prefix = 'Delivered ';
            time = moment.utc(this.delivered_at);
        } else {
            prefix = 'Created ';
            time = moment.utc(this.created_at);
        }

        time = user.state.user.timezone ? time.tz(user.state.user.timezone) : time.local();

        return {
            ownerName: this.user.full_name,
            prefix: prefix,
            time: time.format('h:mm A'),
            date: time.format('ddd, DD MMM, h:mm A'),
            moment: time,
        };
    }

    isNotRetryError() {
        if (this.failed_reason === '30006') return true;
        if (this.hasStatusFailedTrial) return true;

        return false;
    }

    get currentDateOfMessage() {
        if (
            this.isNote ||
          this.isPhoneCall ||
          this.icon === 'icon_outbound_call'
        ) {
            return false;
        } else {
            let createdDate = moment.utc(this.created_at);
            createdDate = this.user.timezone
                ? createdDate.tz(this.user.timezone)
                : createdDate.local();
            createdDate = createdDate.format('ddd, MMM Do, Y');
            let createdLocalTime = moment.utc(this.created_at);
            createdLocalTime = createdLocalTime.local();
            createdLocalTime = createdLocalTime.format('h:mm:ss a');

            const status = this.status;
            let messageStatus = '';
            if (status === 'faked') {
                messageStatus = 'Sent';
            } else if (status === 'failed_trial') {
                messageStatus = 'Failed';
            } else if ((status === 'retry') || (status === 'retry_manual')) {
                messageStatus = 'Queued';
            } else if (status === 'billing_declined') {
                messageStatus = 'Billing declined';
            } else {
                messageStatus = status.charAt(0).toUpperCase() + status.slice(1);
            }

            let message = `${messageStatus} ${createdDate} at ${createdLocalTime}`;

            if (this.hasScheduledBroadCast) {
                message = `Broadcast message </br>${message}`;
            }

            return message;
        }
    }

    getErrorTooltip(isCurrentUserMember = false, onTrial = false) {
        const reason = this.failed_reason || '';
        const retry = !this.isNotRetryError;
        let tooltip = twilioLang[reason] || (reason && reason.endsWith('.') ? reason : `${reason}.`);

        if (reason === 'insufficient_funds') {
            if (onTrial) {
                tooltip = 'You\'re out of trial credits. Please upgrade your account to send messages.';
            } else {
                tooltip = 'You\'re out of credits and your message was not delivered. Please add credits to send messages.';
            }

            return tooltip;
        }

        if (this.hasStatusFailedTrial) {
            if (isCurrentUserMember) {
                return `${tooltip} Please contact your admin to upgrade and begin new chats.`;
            }

            return `${tooltip} <a id="upgrade-plan" href="#">Upgrade</a> to begin new chats.`;
        }

        if (
            (
                reason.includes('21610') ||
                reason === 'contact_hard_opt_out'
            ) && this.contact.opt_out_status !== null
        ) {
            return tooltip;
        }

        if (
            reason.includes('30006') ||
            reason === 'contact_is_yet_to_opt_in'
        ) {
            return `<span class="center dib">${tooltip}</span>`;
        }

        if (reason === 'duplicate_message') {
            tooltip = 'Duplicate message. Message has already been sent to the contact';
        }

        if (retry) {
            return `<span class="center dib">${tooltip}<br /> Click to retry.</span>`;
        }

        return `<span class="center dib">${tooltip}</span>`;
    }

    get messageTooltip() {
        let title = '';

        if (this.hasScheduled) {
            title = 'Message scheduled';
        } else if (this.automated) {
            title = 'Message automatically sent';
        } else if (this.type === 'note') {
            title = 'Message note sent';
        } else if (this.received_at) {
            title = 'Message received';
        } else if (this.failed_at) {
            title = 'Message failed';
        } else if (this.hasStatusQueued) {
            title = 'Message queued';
        } else if (this.hasStatusRetry) {
            title = 'Message queued';
        } else if (this.status === 'sent' || this.status === 'legacy' || this.status === 'faked') {
            title = 'Message sent';
        } else if (this.status === 'delivered') {
            title = 'Message delivered';
        } else {
            title = 'Message created';
        }

        return title;
    }

    get hasScheduled() {
        return this.send_at && (this.send_at > this.sent_at) && !this.hasStatusRetry;
    }

    get hasScheduledBroadCast() {
        return this.source_id > 0 && this.source === 'broadcast';
    }

    get hasStatusFailedTrial() {
        return this.status === 'failed_trial';
    }

    get hasStatusQueued() {
        return this.status === 'queued';
    }

    get hasStatusRetry() {
        return this.status === 'retry' || this.status === 'retry_manual';
    }

    get hasRetryManualStatus() {
        return this.status === 'retry_manual';
    }

    get hasStatusFaked() {
        return this.status === 'faked';
    }

    get hasFilesMedia() {
        return Boolean(this.filesMedia.length);
    }

    get filesMedia() {
        return this.media.filter(item => item.isImage || item.isVideo);
    }

    get isNote() {
        return this.type === 'note';
    }

    get isPhoneCall() {
        return this.type === 'call';
    }

    get isReassignType() {
        return this.type === 'reassign' && this.showMessage;
    }

    get isReceivedType() {
        if (this.isReassignType) return false;
        if (this.isEmptyTranscript) return false;

        return Boolean(this.received_at);
    }

    get isSentType() {
        if (this.isUndoType) return false;
        if (this.isReassignType) return false;
        if (this.isReceivedType) return false;
        if (this.isEmptyTranscript) return false;

        return true;
    }

    get isTranscriptType() {
        if (!this.record) return false;

        return Boolean(this.record.is_transcribe_voice_to_text);
    }

    get isEmptyTranscript() {
        return this.isTranscriptStatus && !this.record;
    }

    get isTranscriptStatus() {
        return this.status === 'transcript' || this.status === 'transcript_failed';
    }

    get isUndoType() {
        return this.type === 'undo';
    }

    get isFailed() {
        return this.failed_at;
    }

    get isStopOnResponse() {
        return this.stop_on_response;
    }

    get isIcon() {
        return this.automated && this.isNote;
    }

    get isDefaultAvatar() {
        if (!this.user.photo_url) return false;

        return this.user.photo_url.includes(
            `${window.location.hostname}/images/app/default-avatar.png`
        );
    }

    get isMention() {
        if (!this.body) {
            return false;
        } else {
            return this.body.includes('class="mention');
        }
    }

    get isServerCommands() {
        // List Commands
        // ['STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT', 'TESTQUIT'];
        // ['START', 'YES', 'UNSTOP', 'Y'];
        // ['HELP', 'INFO'];

        const status = this.body.toLowerCase();

        return Boolean(
            status === 'start' ||
            status === 'stop' ||
            status === 'stopall' ||
            status === 'unsubscribe' ||
            status === 'cancel' ||
            status === 'end' ||
            status === 'quit' ||
            status === 'testquit' ||
            status === 'yes' ||
            status === 'unstop' ||
            status === 'y' ||
            status === 'help' ||
            status === 'info'
        );
    }

    handleUpdateProps(props) {
        for (let key in props) {
            this[key] = props[key];
        }
    }

    getParsedShortUrls(shortenerDomains) {
        const bodyRaw = (this.body_raw || '').replaceAll('\'', ' ');

        const urls = bodyRaw.match(urlRegex({ strict: false }));
        return (urls || []).filter(url => shortenerDomains.some(domainName => url.includes(domainName)));
    }
}
